import * as React from "react"
import {useEffect, useState} from "react"
import {DrawerSeeker} from "@ui-seeker/core"
import {Box, Typography, Chip, Grid, Paper, Divider} from "@mui/material"
import {getSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {selectedBatchForDetails, batchesTableData, selectedBatchForProducts} from "../@signals"


// Friendly labels for config keys
const CONFIG_LABELS = {
	dateField: 'Date Filter',
	dateStart: 'Date Start',
	dateEnd: 'Date End',
	splitByBrands: 'Split By Brands'
}

// Render "Name (id)" when the back resolved a name for that ID, bare ID otherwise
const withName = (names, id) => {
	const name = names?.[String(id)]
	return name ? `${name} (${id})` : id
}

// Friendly values for specific config fields
const formatConfigValue = (key, value) => {
	if (key === 'dateField') {
		return value === 'first_date' ? 'First seen (new products)' : 'Last seen (active products)'
	}
	return value
}

const ConfigItem = ({label, value, configKey, isArray = false, isNested = false, names = null}) => {
	if (value === null || value === undefined) return null

	// Format value using the configKey if provided
	const displayValue = configKey ? formatConfigValue(configKey, value) : value

	// Handle nested objects
	if (!isArray && typeof value === 'object' && !Array.isArray(value)) {
		return (
			<Box sx={{ mb: 2 }}>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
					{label}
				</Typography>
				<Paper elevation={0} sx={{ p: 1.5, bgcolor: 'action.hover', borderLeft: 3, borderColor: 'primary.main' }}>
					<Grid container spacing={1}>
						{Object.entries(value).map(([key, val]) => {
							const nestedLabel = CONFIG_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
							return (
								<Grid item xs={12} key={key}>
									<ConfigItem
										label={nestedLabel}
										value={val}
										configKey={key}
										isArray={Array.isArray(val)}
										isNested={true}
									/>
								</Grid>
							)
						})}
					</Grid>
				</Paper>
			</Box>
		)
	}

	return (
		<Box sx={{ mb: isNested ? 1 : 2 }}>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
				{label}
			</Typography>
			{isArray && Array.isArray(value) ? (
				<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
					{value.length > 0 ? (
						value.map((item, idx) => (
							<Chip
								key={idx}
								label={typeof item === 'object'
									? (item.name || item.label || item.value || JSON.stringify(item))
									: withName(names, item)}
								size="small"
								variant="outlined"
							/>
						))
					) : (
						<Typography variant="body2" color="text.secondary">-</Typography>
					)}
				</Box>
			) : (
				<Typography variant="body2" fontWeight={isNested ? "normal" : "medium"}>
					{typeof displayValue === 'boolean'
						? (displayValue ? 'Yes' : 'No')
						: names ? withName(names, displayValue) : displayValue}
				</Typography>
			)}
		</Box>
	)
}


export default function BatchDetailsDrawer() {
	const isOpen = selectedBatchForDetails.value !== null
	const batchData = isOpen
		? batchesTableData.value.find(b => b.batchID === selectedBatchForDetails.value)
		: null
	const config = batchData?.config || null
	const batchID = batchData?.batchID ?? null

	// Names behind the config IDs (brands, sites, SKUs, tags, customer): {configKey: {id: name}}
	const [configLabels, setConfigLabels] = useState({})

	useEffect(() => {
		if (!batchID) {
			setConfigLabels({})
			return
		}

		let cancelled = false
		getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${batchID}/config-labels`)
			.then(res => { if (!cancelled) setConfigLabels(res || {}) })
			.catch(() => { if (!cancelled) setConfigLabels({}) })  // silent: IDs stay bare

		return () => { cancelled = true }
	}, [batchID])

	const handleClose = () => {
		selectedBatchForDetails.value = null
		selectedBatchForProducts.value = null
	}
	
	if (!isOpen || !batchData) return null
	
	return (
		<DrawerSeeker
			isOpen={isOpen}
			controller={handleClose}
			title={`Batch Details - ${batchData.name || `#${batchData.batchID}`}`}
			width="50%"
			hideButtons
		>
			<Grid container spacing={3}>
				{/* Batch Info */}
				<Grid item xs={12}>
					<Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
						<Typography variant="h6" gutterBottom>Batch Information</Typography>
						<Divider sx={{ mb: 2 }} />
						
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<ConfigItem label="Batch ID" value={batchData.batchID} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Name" value={batchData.name} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Status" value={batchData.status?.toUpperCase()} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Progress" value={`${batchData.progress?.toFixed(1)}%`} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Total Tasks" value={batchData.totalTasks} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Completed Tasks" value={batchData.completedTasks} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Failed Tasks" value={batchData.failedTasks} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Created At" value={batchData.createdAt ? new Date(batchData.createdAt).toLocaleString() : '-'} />
							</Grid>
						</Grid>
					</Paper>
				</Grid>
				
				{/* Statistics */}
				<Grid item xs={12}>
					<Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
						<Typography variant="h6" gutterBottom>Statistics</Typography>
						<Divider sx={{ mb: 2 }} />
						
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<ConfigItem label="Automapped EAN" value={batchData.automappedEan || 0} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Automapped IA" value={batchData.automappedIa || 0} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Autoexcluded" value={batchData.autoexcluded || 0} />
							</Grid>
							<Grid item xs={6}>
								<ConfigItem label="Pending QA" value={batchData.pendingQa || 0} />
							</Grid>
						</Grid>
					</Paper>
				</Grid>
				
				{/* Configuration */}
				<Grid item xs={12}>
					<Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
						<Typography variant="h6" gutterBottom>Configuration</Typography>
						<Divider sx={{ mb: 2 }} />
						
						{config ? (
							<Grid container spacing={2}>
								{Object.entries(config).map(([key, value]) => {
									// Skip null/undefined
									if (value === null || value === undefined) return null

									// Format key to readable label (use CONFIG_LABELS if available)
									const label = CONFIG_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

									return (
										<Grid item xs={6} key={key}>
											<ConfigItem
												label={label}
												value={value}
												configKey={key}
												isArray={Array.isArray(value)}
												names={configLabels[key] || null}
											/>
										</Grid>
									)
								})}
							</Grid>
						) : (
							<Typography variant="body2" color="text.secondary">
								No configuration available
							</Typography>
						)}
					</Paper>
				</Grid>
			</Grid>
		</DrawerSeeker>
	)
}
